/* Keep Dawn's predictive search in the visible field. Only dismissal is added;
   native queries, caching, reset, result navigation and analytics stay intact. */
class BDInlineSearch extends HTMLElement {
  connectedCallback() {
    this.compactHeader = new BDCompactHeader(this);
    this.onKeydown = (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      const search = this.querySelector('predictive-search');
      if (!search?.isOpen) return;
      event.preventDefault();
      event.stopPropagation();
      // Focus before closing: native onFocus may reopen cached suggestions.
      search.input.focus();
      search.close();
    };
    this.onOutsidePointer = (event) => {
      if (!this.contains(event.target)) this.querySelector('predictive-search')?.close?.();
    };
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onOutsidePointer);
  }

  disconnectedCallback() {
    this.compactHeader?.disconnect();
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onOutsidePointer);
  }
}

/* A mobile presentation of the native sticky header, with the same input,
   drawer, account and bag. The outer flow height never changes on scroll. */
class BDCompactHeader {
  constructor(search) {
    this.search = search;
    this.wrapper = search.closest('sticky-header');
    this.section = this.wrapper?.closest('.section-header');
    this.header = search.closest('.bd-header--search-first');
    if (!this.header || !this.section) return;
    this.input = search.querySelector('input[type="search"]');
    this.prompt = this.input?.placeholder || '';
    this.submit = search.querySelector('.search__button');
    this.inputTabIndex = this.input?.getAttribute('tabindex');
    this.inputAriaHidden = this.input?.getAttribute('aria-hidden');
    this.submitLabel = this.submit?.getAttribute('aria-label') || 'Search';
    this.brand = this.header.querySelector('bd-header-brand');
    this.media = matchMedia('(max-width: 989px)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.abort = new AbortController();
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.setExpanded(!this.wantsCompact, true);
    }, {signal: this.abort.signal});
    this.lastY = Math.max(0, window.scrollY);
    this.travel = 0;
    this.width = window.innerWidth;
    this.compactFlowHeight = this.section.hasAttribute('data-bd-header-restored')
      ? parseFloat(this.section.style.getPropertyValue('--bd-expanded-header-height')) : null;
    this.updateMode = this.updateMode.bind(this);
    this.media.addEventListener('change', this.updateMode, {signal: this.abort.signal});
    window.addEventListener('resize', () => {
      // Opening the software keyboard changes height, not the header layout.
      if (this.width === window.innerWidth) return;
      this.width = window.innerWidth;
      this.updateMode();
    }, {signal: this.abort.signal});
    window.addEventListener('pagehide', () => {
      // A back/forward-cache snapshot must contain a settled header, never
      // a paused intermediate grid or a video waiting for its next frame.
      this.setExpanded(!this.wantsCompact, true);
    }, {signal: this.abort.signal});
    this.header.addEventListener('focusin', event => {
      // Merely tabbing onto the compact magnifier must leave it as a button.
      if (this.search.contains(event.target) && event.target !== this.submit) this.setExpanded(true);
    }, {signal: this.abort.signal});
    this.search.addEventListener('click', event => {
      if (!this.submit?.contains(event.target)) return;
      if (!this.wantsCompact && this.input?.value.trim()) return;
      // Native click covers pointer, Enter and Space. Prevent the same event
      // becoming a form submission when we restore the expanded submit button.
      event.preventDefault();
      this.setExpanded(true);
      this.input?.focus({preventScroll: true});
    }, {signal: this.abort.signal});
    // Dawn's predictive-search keyup handler prevents the native Space click.
    // Restore button activation only in compact mode; input keys stay native.
    this.submit?.addEventListener('keydown', event => {
      if (this.wantsCompact && event.code === 'Space') event.preventDefault();
    }, {signal: this.abort.signal});
    this.submit?.addEventListener('keyup', event => {
      if (!this.wantsCompact || event.code !== 'Space') return;
      event.preventDefault();
      event.stopPropagation();
      this.submit.click();
    }, {signal: this.abort.signal});
    this.search.addEventListener('keyup', event => {
      // Native option navigation assumes visible results. During the morph
      // keep arrow keys out of that hidden list; normal typing stays native.
      if ((this.wantsCompact || this.transitioning) && ['ArrowUp', 'ArrowDown'].includes(event.code)) event.stopPropagation();
    }, {capture: true, signal: this.abort.signal});
    // Expand before Dawn measures the menu drawer's lower anchor.
    this.header.addEventListener('click', event => {
      if (event.target.closest('header-drawer > details > summary')) this.setExpanded(true, true);
    }, {capture: true, signal: this.abort.signal});
    this.resize = new ResizeObserver(() => {
      // Animated field height is not a new header layout. Keep the reserved
      // page space fixed until the expanded layout has finished settling.
      if (this.media.matches && !this.transitioning && this.section.dataset.bdHeaderState === 'expanded') this.measure();
    });
    this.resize.observe(this.header);
    this.updateMode();
  }

  measure() {
    // Measure the layout, not the temporarily animated wrapper surface.
    this.expandedHeight = this.header.offsetHeight + parseFloat(getComputedStyle(this.wrapper).borderBottomWidth || 0);
    this.section.style.setProperty('--bd-expanded-header-height', `${this.compactFlowHeight ?? this.expandedHeight}px`);
    document.documentElement.style.setProperty('--header-height', `${this.expandedHeight}px`);
  }

  updateMode() {
    const compact = this.media.matches && this.section.dataset.bdHeaderState === 'compact';
    this.cancelReveal();
    this.cancelTransition();
    this.sequence = (this.sequence || 0) + 1;
    this.travel = 0;
    this.lastY = Math.max(0, window.scrollY);
    this.wantsCompact = compact;
    this.section.classList.remove('shopify-section-header-hidden', 'animate');
    this.section.classList.toggle('bd-mobile-header', this.media.matches);
    this.brand?.setWordmarkExpanded?.(!compact, {immediate: true});
    this.setSearchMode(!compact);
    if (this.media.matches) {
      this.section.dataset.bdHeaderState = 'expanded';
      this.section.classList.add('shopify-section-header-sticky');
      this.measure();
      // offsetTop stays stable even while the section is stuck at the top.
      this.collapseAfter = this.section.offsetTop + this.expandedHeight + 32;
      if (compact) this.section.dataset.bdHeaderState = 'compact';
      if (compact && this.compactFlowHeight != null) {
        this.compactHeight = this.compactFlowHeight = this.wrapper.offsetHeight;
        this.section.style.setProperty('--bd-expanded-header-height', `${this.compactFlowHeight}px`);
      }
      document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
    } else {
      this.compactFlowHeight = null;
      delete this.section.dataset.bdHeaderState;
      this.section.style.removeProperty('--bd-expanded-header-height');
      document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
    }
  }

  rememberState() {
    try {
      if (this.wantsCompact) sessionStorage.setItem('bd-mobile-header-v37', JSON.stringify({height: this.expandedHeight, compactHeight: this.compactHeight || 65}));
      else sessionStorage.removeItem('bd-mobile-header-v37');
    } catch { /* Header interaction remains available without storage. */ }
  }

  cancelReveal() {
    clearTimeout(this.revealIntent);
    this.revealIntent = null;
  }

  onScroll(scrollTop) {
    if (!this.header || !this.media.matches) return false;
    const y = Math.max(0, scrollTop);
    const delta = y - this.lastY;
    this.lastY = y;
    if (delta && Math.sign(delta) !== Math.sign(this.travel)) this.travel = 0;
    this.travel += delta;
    const editing = document.activeElement !== this.submit && this.search.contains(document.activeElement);
    const menuOpen = this.section.classList.contains('menu-open');
    // At enlarged text sizes, keep the accessible two-row header visible.
    const fits = this.header.clientWidth >= parseFloat(getComputedStyle(document.documentElement).fontSize) * 30;
    // An actual upward arrival at the top opens the full header. Merely
    // restoring a compact page at y=0 must preserve its first-paint state.
    if ((y === 0 && delta < 0) || editing || menuOpen || !fits) {
      this.cancelReveal();
      this.setExpanded(true);
    } else if (this.transitioning) {
      // Commit each scroll transition. Tiny reversals cannot restart the motion
      // or flip the grid repeatedly; direct search/menu actions still win.
      this.cancelReveal();
      this.travel = 0;
    } else if (delta > 0) {
      this.cancelReveal();
      // Downward intent is measured from the last direction change, not from
      // the header boundary. Starting at the top therefore collapses at about
      // 300px; revealing the full header mid-page uses the same short travel.
      if (this.travel >= 300 && y > this.collapseAfter) this.setExpanded(false);
    } else if (this.wantsCompact && this.travel <= -1500 && !this.revealIntent) {
      // Away from the top, require sustained upward travel plus brief intent.
      // Search/Menu open explicitly; resumed downward scrolling cancels intent.
      this.revealIntent = setTimeout(() => {
        this.revealIntent = null;
        if (this.travel <= -1500 && !this.transitioning) this.setExpanded(true);
      }, 300);
    }
    return true;
  }

  setSearchMode(expanded) {
    if (!this.input || !this.submit) return;
    const restore = (element, name, value) => value === null ? element.removeAttribute(name) : element.setAttribute(name, value);
    this.input.placeholder = this.prompt;
    this.submit.type = expanded ? 'submit' : 'button';
    this.submit.setAttribute('aria-label', expanded ? this.submitLabel : 'Open Search');
    if (expanded) {
      restore(this.input, 'tabindex', this.inputTabIndex);
      restore(this.input, 'aria-hidden', this.inputAriaHidden);
      this.submit.removeAttribute('aria-expanded');
      this.submit.removeAttribute('aria-controls');
    } else {
      this.input.setAttribute('tabindex', '-1');
      this.input.setAttribute('aria-hidden', 'true');
      this.submit.setAttribute('aria-expanded', 'false');
      this.submit.setAttribute('aria-controls', this.input.id);
      this.search.querySelector('predictive-search')?.close?.();
    }
  }

  easeInOutCubic(progress) {
    return progress < .5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
  }

  currentPose() {
    if (!this.poseAnimation) return this.wantsCompact ? 1 : 0;
    const duration = this.poseAnimation.effect.getTiming().duration;
    const time = Math.max(0, Math.min(1, (this.poseAnimation.currentTime || 0) / duration));
    const eased = this.easeInOutCubic(time);
    return this.poseFrom + (this.poseTo - this.poseFrom) * eased;
  }

  cancelTransition() {
    this.animations?.forEach(animation => animation.cancel());
    this.animations = [];
    this.poseAnimation = null;
    this.transitioning = false;
    this.brand?.removeAttribute('data-wordmark-playing');
    this.section?.removeAttribute('data-bd-header-moving');
  }

  setExpanded(expanded, immediate = false) {
    if (!this.media?.matches) return;
    this.cancelReveal();
    if (this.wantsCompact === !expanded && !immediate) return;
    const poseFrom = this.currentPose();
    // A late image decode must not switch a running fallback wipe into a
    // different visual renderer when Search reverses the transition.
    const useGlyphs = this.transitioning ? this.wordmarkUsesGlyphs : this.brand?.wordmarkReady;
    // WebKit's composited image clip can trail the animation clock by a frame.
    // A fallback reversal must start from the visible wipe, not a newer clock
    // position, even when the optional glyph artwork has just become ready.
    const artwork = this.brand?.querySelector(':scope > img');
    let fallbackClipFrom;
    if (this.transitioning && !useGlyphs && artwork) {
      const clip = getComputedStyle(artwork).clipPath;
      const inset = clip.match(/^inset\(\s*[^ ]+\s+(-?\d+(?:\.\d+)?)%\s/);
      if (clip === 'none') fallbackClipFrom = 0;
      else if (inset) fallbackClipFrom = Number(inset[1]);
    }
    // A newly loaded compact document reserves one row from its first paint.
    // If Search opens at the top, grow that space with the same animation;
    // scrolled transitions retain their original flow baseline.
    const flowFrom = this.section.offsetHeight;
    const growFlow = expanded && this.compactFlowHeight != null && window.scrollY < 24;
    if (growFlow) this.compactFlowHeight = null;
    this.wantsCompact = !expanded;
    const sequence = this.sequence = (this.sequence || 0) + 1;
    this.cancelTransition();
    this.setSearchMode(expanded);
    const noMotion = immediate || this.motion.matches;
    const state = expanded ? 'expanded' : 'compact';
    if (noMotion) {
      this.section.dataset.bdHeaderState = state;
      if (expanded) this.measure();
      document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
      this.brand?.setWordmarkExpanded?.(expanded, {immediate: true});
      if (!expanded) this.compactHeight = this.wrapper.offsetHeight;
      this.rememberState();
      this.travel = 0;
      return;
    }

    this.section.dataset.bdHeaderMoving = '';
    const controls = [this.brand, this.header.querySelector('header-drawer'), this.header.querySelector('.header__icons')].filter(Boolean);
    // Read the two stable layouts synchronously, before the browser can paint.
    // One normalized pose drives every moving element in both directions.
    const measurePose = mode => {
      this.section.dataset.bdHeaderState = mode;
      if (mode === 'expanded') this.measure();
      return {search: this.search.getBoundingClientRect(), height: this.wrapper.offsetHeight,
        controls: controls.map(element => element.getBoundingClientRect())};
    };
    const full = measurePose('expanded'), compact = measurePose('compact');
    this.compactHeight = compact.height;
    this.rememberState();
    this.section.dataset.bdHeaderState = state;
    const target = expanded ? full : compact;
    document.documentElement.style.setProperty('--header-height', `${target.height}px`);
    this.poseFrom = poseFrom;
    this.poseTo = expanded ? 0 : 1;
    this.transitioning = true;
    const duration = 500;
    const mix = (a, b, p) => a + (b - a) * p;
    // One cubic pose drives both edges and the magnifier. There is no arc or
    // faster secondary width curve to make the field race ahead of the icon.
    const textFadeEnd = Math.min(.04,
      Math.max(1, parseFloat(getComputedStyle(this.input).paddingLeft) - 2)
      / Math.max(1, Math.abs(compact.search.left - full.search.left)));
    this.wordmarkUsesGlyphs = Boolean(useGlyphs);
    const clipFrom = fallbackClipFrom ?? 73.333333 * this.poseFrom;
    const clipTo = 73.333333 * this.poseTo;
    const glyphs = useGlyphs ? this.brand.wordmarkGlyphs : [];
    const frames = {search: [], surface: [], field: [], input: [], button: [], wordmark: [], glyphs: glyphs.map(() => []), controls: controls.map(() => [])};
    const buttonColor = getComputedStyle(this.search).getPropertyValue("--bd-blue").trim();
    for (let frame = 0; frame <= 80; frame++) {
      const offset = frame / 80;
      const eased = this.easeInOutCubic(offset);
      const pose = mix(this.poseFrom, this.poseTo, eased);
      // Clear the surface before it enters the top-row controls. Geometry
      // continues along the same path after the background has faded.
      const surface = Math.max(0, 1 - pose / .4) ** 2;
      const width = mix(full.search.width, compact.search.width, pose);
      const right = mix(full.search.right, compact.search.right, pose);
      const left = right - width;
      const top = mix(full.search.top, compact.search.top, pose);
      // Fade the native text before the shrinking edge reaches its first
      // character. Its full-width text layout stays anchored in place, so a
      // long query never gets squeezed, dragged or rewrapped during motion.
      const textProgress = Math.min(1, pose / textFadeEnd);
      const textOpacity = 1 - textProgress ** 2 * (3 - 2 * textProgress);
      // Keep position and width in one layout sample. WebKit can otherwise
      // composite translate before the right-anchored button has reflowed,
      // creating a small backwards step on wider mobile headers.
      frames.search.push({offset, left: `${left - target.search.left}px`, top: `${top - target.search.top}px`, width: `${width}px`});
      frames.surface.push({offset, height: `${mix(full.height, compact.height, pose)}px`});
      frames.field.push({offset, backgroundColor: `rgba(245,245,245,${surface})`, borderColor: `rgba(235,235,235,${surface})`, height: `${mix(full.search.height, compact.search.height, pose)}px`, minHeight: `${mix(full.search.height, compact.search.height, pose)}px`});
      frames.input.push({offset, opacity: textOpacity, width: `${full.search.width - 2}px`, height: `${full.search.height - 2}px`, flexShrink: 0, left: `${full.search.left - left}px`, top: `${full.search.top - top}px`});
      frames.wordmark.push({offset, clipPath: `inset(0 ${mix(clipFrom, clipTo, eased)}% 0 0)`});
      if (glyphs.length) this.brand.wordmarkOffsets(pose).forEach((dy, index) => {
        frames.glyphs[index].push({offset, transform: `translateY(${dy}px)`});
      });
      frames.button.push({offset, right: `${-pose}px`, top: `${-pose}px`, color: buttonColor});
      controls.forEach((element, index) => {
        frames.controls[index].push({offset, transform: `translate(${mix(full.controls[index].left, compact.controls[index].left, pose) - target.controls[index].left}px, ${mix(full.controls[index].top, compact.controls[index].top, pose) - target.controls[index].top}px)`});
      });
    }
    const animate = (element, keyframes) => {
      // Cubic easing is already sampled into every keyframe above. Applying
      // it again here would distort the path and desynchronize interruptions.
      const animation = element.animate(keyframes, {duration, fill: 'both', easing: 'linear'});
      animation.pause();
      animation.finished.catch(() => {});
      this.animations.push(animation);
      return animation;
    };
    this.poseAnimation = animate(this.search, frames.search);
    if (growFlow) animate(this.section, frames.surface.map(({offset}) => {
      const eased = this.easeInOutCubic(offset);
      return {offset, height: `${mix(flowFrom, this.expandedHeight, eased)}px`};
    }));
    animate(this.wrapper, frames.surface);
    animate(this.search.querySelector('.field'), frames.field);
    animate(this.input, frames.input);
    animate(this.submit, frames.button);
    controls.forEach((element, index) => animate(element, frames.controls[index]));
    // Commit the endpoint underneath the animation before any cancellation.
    // Reversals reuse currentPose(), so the same still never swaps or restarts.
    this.brand?.setWordmarkExpanded?.(expanded);
    if (glyphs.length) {
      this.brand.setAttribute('data-wordmark-playing', '');
      glyphs.forEach((glyph, index) => animate(glyph, frames.glyphs[index]));
    } else if (artwork) animate(artwork, frames.wordmark);
    const now = document.timeline.currentTime;
    this.animations.forEach(animation => {
      animation.play();
      animation.startTime = now;
    });
    Promise.all(this.animations.map(animation => animation.finished)).then(() => {
      if (sequence !== this.sequence) return;
      this.cancelTransition();
      this.travel = 0;
      this.lastY = Math.max(0, window.scrollY);
    }).catch(() => {});
  }

  disconnect() {
    this.cancelReveal();
    this.cancelTransition();
    this.setSearchMode(true);
    this.abort?.abort();
    this.resize?.disconnect();
    this.brand?.setWordmarkExpanded?.(true, {immediate: true});
  }
}

if (!customElements.get('bd-inline-search')) customElements.define('bd-inline-search', BDInlineSearch);
